import styled, { css } from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import { mixins, fonts, colors } from '@custom-components/css';
import { SVGIcon } from '../SVGIcon';

/**
 * Define properties for different button themes.
 * The styles are written in SCSS format using template literals tagged with the
 * `css` helper from styled-components
 */
export const buttonThemes = {
  primary: css`
    ${({ theme }) => `background-color: ${theme.highlightTheme}`};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      ${({ theme }) => `background-color: ${theme.midTheme}`};
      color: ${colors.colorWhite};
    }
  `,
  secondary: css`
    ${({ theme }) => `background-color: ${theme.lightText}`};
    ${({ theme }) => `border-color: ${theme.lightText}`};
    ${({ theme }) => `color: ${theme.midTheme}`};
    &:hover {
      cursor: pointer;
      background-color: ${colors.colorGrey04};
    }
  `,
  tertiary: css`
    background-color: ${colors.colorBlue};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      background-color: ${colors.colorBlue02};
    }
  `,
  quaternary: css`
    background-color: ${colors.colorGreen};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      background-color: #118a53;
    }
  `,
  dark: css`
    ${({ theme }) => `background-color: ${theme.midTheme}`};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      ${({ theme }) => `background-color: ${theme.mainTheme}`};
      ${({ theme }) => `color: ${theme.lightText}`};
    }
  `,
  darker: css`
    ${({ theme }) => `background-color: ${theme.midTheme}`};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      ${({ theme }) => `background-color: ${theme.darkTheme}`};
      ${({ theme }) => `color: ${theme.lightText}`};
    }
  `,
  disabled: css`
    background-color: ${colors.colorGrey04};
    color: ${colors.colorGrey03};
    border: 0;
    &:hover {
      background-color: ${colors.colorGrey04};
      color: ${colors.colorGrey03};
      cursor: auto;
    }
  `,
  blank: css`
    background-color: transparent;
    color: ${colors.colorBlue};
    &:hover {
      background-color: ${colors.colorGrey04};
      color: ${colors.colorBlue};
      cursor: pointer;
    }
  `,
  danger: css`
    ${({ theme }) => `background-color: ${theme.error}`};
    color: ${colors.colorWhite};
    &:hover {
      cursor: pointer;
      ${({ theme }) => `background-color: ${theme.darkError}`};
      ${({ theme }) => `color: ${theme.lightText}`};
    }
  `,
  multiSearchSelect: css`
    margin: 0 !important;
    width: -webkit-fill-available;
    border: 1px solid rgba(0, 0, 0, 0.23);
    text-transform: none !important;
    padding: 7.5px 14px !important;
    justify-content: flex-start !important;
    letter-spacing: -0.1px !important;
    &:hover {
      cursor: pointer;
      border: 1px solid black;
    }
    span {
      font-size: 0.9rem;
      white-space: nowrap;
      overflow: auto;
      text-overflow: ellipsis;
    }
  `,
  outlined: css`
    background-color: transparent;
    color: ${colors.colorBlue};
    border: 1px solid ${colors.colorBlue};
    &:hover {
      background-color: ${colors.colorGrey05};
      color: ${colors.colorBlue};
      cursor: pointer;
    }
  `
};
export const buttonThemeNames = Object.keys(buttonThemes);
export const DEFAULT_THEME = buttonThemeNames[0];

export const iconThemes = {
  primary: {
    iconColor: `${colors.colorWhite}`,
    backgroundColor: `${({ theme }) => `${theme.highlightTheme}`}`,
    hover: {
      iconColor: `${colors.colorWhite}`,
      backgroundColor: `${({ theme }) => `${theme.midTheme}`}`
    }
  },
  secondary: {
    iconColor: `${({ theme }) => `${theme.midTheme}`}`,
    backgroundColor: `${({ theme }) => `${theme.lightText}`}`,
    hover: {
      iconColor: `${({ theme }) => `${theme.midTheme}`}`,
      backgroundColor: `${colors.colorGrey04}`
    }
  },
  disabled: {
    iconColor: `${colors.colorGrey03}`,
    backgroundColor: `${colors.colorGrey04}`,
    hover: {
      iconColor: `${colors.colorGrey03}`,
      backgroundColor: `${colors.colorGrey04}`
    }
  }
};
export const iconThemeNames = Object.keys(iconThemes);
export const DEFAULT_ICON = iconThemeNames[0];

/**
 * Wrapper that gives the tooltip an anchor for absolute positioning and
 * reveals the tooltip on hover via a CSS sibling selector.
 */
export const StyledButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  & > button:hover ~ [data-tooltip] {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    prop !== 'fullWidth' &&
    prop !== 'btnTheme' &&
    prop !== 'reqPermission' &&
    prop !== 'uppercase' &&
    prop !== 'loading' &&
    prop !== 'iconTheme' &&
    prop !== 'tooltip'
})`
  /* Properties controlled by theme prop */
  ${({ btnTheme }) => buttonThemes[btnTheme]}
  /** If the button is disabled, use that theme instead */
  ${({ disabled }) => disabled && buttonThemes.disabled}

  ${({ fullWidth }) => fullWidth && `width: 100%`};

  /** default styling */
  margin: ${mixins.pxToRem(5)};
  min-height: ${mixins.pxToRem(30)};
  padding: ${mixins.pxToRem(5)} ${mixins.pxToRem(20)};
  font-family: ${fonts.fontRegular};
  font-size: ${mixins.pxToRem(12)};
  letter-spacing: 1px;
  text-decoration: none;
  text-transform: uppercase;
  user-select: none;
  outline: none;
  transition: background-color 125ms;
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  border-radius: ${mixins.pxToRem(5)};
  .icon {
    margin-right: ${mixins.pxToRem(5)};
  }
  &:hover {
    text-decoration: none;
  }

  ${({ hidden }) => hidden && `display: none`}
`;

export const StyledDiv = styled.div`
  /* Tooltip — anchored to StyledButtonWrapper, shown on parent hover */
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 16rem;
  padding: 8px 10px;
  background-color: white;
  border: 1px solid ${colors.colorGrey04};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: ${mixins.pxToRem(12)};
  font-family: ${fonts.fontRegular};
  line-height: 1.4;
  color: ${colors.colorGrey03};
  z-index: 12;
  white-space: normal;
  word-break: break-word;
  opacity: 0;
  transition: opacity 150ms ease;
  pointer-events: none;
`;

export const NeutralAnchor = styled.a`
  text-decoration: none;
  font-size: ${mixins.pxToRem(12)};
`;

export const StyledIcon = styled(SVGIcon).withConfig({
  shouldForwardProp: (prop) => prop !== 'iconTheme'
})`
  transition: background-color 125ms;
  width: 22px;
  height: 22px;
  ${({ iconTheme }) => iconThemes[iconTheme]};
`;

export const StyledSpinner = styled(CircularProgress)`
  && {
    color: white;
    margin: 0;
  }
`;

export const SpinnerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${mixins.pxToRem(5)};
`;
